import React from 'react';
import { HelpTip, Divider, Text, FileUploadButton } from '@amzn/storm-ui';
import { useTranslation } from 'react-i18next';
import { CustomButton } from '../../../UiComponentsLibrary/CustomButton';
import { CustomButtonColor } from '../../../UiComponentsLibrary/CustomButton/constants/CustomButtonColor';
import { CustomAlert } from '../../../UiComponentsLibrary/CustomAlert/CustomAlert';
import CustomSpinner from '../../../UiComponentsLibrary/CustomSpinner';
import { StatusTypes } from '../../../Shared/constants/statusTypes';
import {
  UploadContainer,
  DownloadContainer,
  DownloadLink,
  ExcelIcon,
  ErrorText,
  FileNameText,
  CustomAlertErrorWrapper,
} from './FileUploadComponent.styles';
import { EXCEL_ICON_LINK } from '../../constants/FileUploadConstants';
import { useFileUpload } from './hooks/useFileUpload';

export type FileUploadComponentProps = {
  helpTip: string;
  templateText: string;
  downloadLinkHref: string;
  downloadLinkText: string;
  uploadButtonLabel: string;
  type: string;
};

const { SUCCESS, ERROR } = StatusTypes;

export const FileUploadComponent = (props: FileUploadComponentProps) => {
  const {
    helpTip,
    templateText,
    downloadLinkHref,
    downloadLinkText,
    type,
    uploadButtonLabel,
  } = props;
  const { t } = useTranslation();

  const {
    fileState,
    uploadState,
    selectedArtEventId,
    handleFileChange,
    uploadCsvFile,
    handleDownloadErrorCSV,
    parseBackendErrors,
  } = useFileUpload(type);

  return (
    <>
      <UploadContainer>
        <div>
          <FileUploadButton
            inputId="file-uploader"
            label={uploadButtonLabel}
            onChange={handleFileChange}
          />
          <HelpTip>{helpTip}</HelpTip>
          {fileState.error && (
            <CustomAlert
              variant="error"
              alertMessage={t('file-upload-failed-header-message')}
            >
              <ErrorText>{fileState.error}</ErrorText>
              {fileState.csvErrorData && (
                <DownloadLink onClick={handleDownloadErrorCSV}>
                  {t('download-error-report-link')}
                </DownloadLink>
              )}
            </CustomAlert>
          )}
          {fileState.fileName && <FileNameText>{fileState.fileName}</FileNameText>}
        </div>
        <DownloadContainer>
          <Text>{templateText}</Text>
          <DownloadLink href={downloadLinkHref}>
            <ExcelIcon src={EXCEL_ICON_LINK} alt="excel-image-icon" />
            {downloadLinkText}
          </DownloadLink>
        </DownloadContainer>
      </UploadContainer>

      <Divider />
      <CustomButton
        text={t('file-upload-excel-button')}
        backgroundColor={CustomButtonColor.uploadCSV}
        handleOnClickCb={uploadCsvFile}
        disabled={!fileState.fileData || !selectedArtEventId}
      />
      {uploadState.isPending && <CustomSpinner />}
      {uploadState.isError &&
        (uploadState.error.statusCode === 500 ? (
          <CustomAlert
            variant={ERROR}
            alertMessage={t('excel-file-upload-failed')}
          />
        ) : (
          <CustomAlertErrorWrapper>
            <CustomAlert
              variant={ERROR}
              alertMessage={t('file-upload-failed-header-message')}
            >
              <ErrorText>
                {fileState.csvErrorData
                  ? t('file-validation-failed-download-report')
                  : parseBackendErrors(uploadState.error)
                      .map((error: string, index: number) => `${index + 1}. ${error}`)
                      .join('\n')}
              </ErrorText>
              {fileState.csvErrorData && (
                <DownloadLink onClick={handleDownloadErrorCSV}>
                  {t('download-error-report-link')}
                </DownloadLink>
              )}
            </CustomAlert>
          </CustomAlertErrorWrapper>
        ))}
      {!selectedArtEventId && (
        <CustomAlert
          variant="info"
          alertMessage={t('create-art-event-info-message')}
        />
      )}
      {uploadState.isSuccess && (
        <CustomAlert
          variant={SUCCESS}
          alertMessage={t('excel-file-upload-successful')}
        />
      )}
    </>
  );
};
